





















































import Vue from "vue";
import { GiftcardObject } from "./types/index";
import GiftCard from "@/components/vue-components/GiftCard.vue";
import LoadMore from "@/components/vue-components/LoadMore.vue";
import { getGiftcardStatus, GIFTCARD_STATUSES } from "@/utils/giftcards";

const PAGE_SIZE = 6;

interface FilterOption {
  value: string;
  label: string;
}

export default Vue.extend({
  name: "GiftcardsDashboard",
  components: {
    GiftCard,
    LoadMore
  },
  props: {
    giftcards: {
      type: Array as () => GiftcardObject[],
      default() {
        return [];
      }
    },
    companies: {
      type: Object,
      default() {
        return {};
      }
    },
    offices: {
      type: Object,
      default() {
        return {};
      }
    },
    resources: {
      type: Object,
      default() {
        return {};
      }
    },
    services: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      filter: "all",
      pageSize: PAGE_SIZE,
      visibleCount: PAGE_SIZE
    };
  },
  computed: {
    /** The API can return gaps in the flattened per-customer results */
    validGiftcards(): GiftcardObject[] {
      return this.giftcards.filter(Boolean);
    },

    /**
     * Only offer states that are actually present, so the filter never leads
     * to an empty list. Counts save a round trip of guessing.
     */
    filterOptions(): FilterOption[] {
      const counts = this.validGiftcards.reduce((acc, giftcard) => {
        const status = getGiftcardStatus(giftcard);
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const present = GIFTCARD_STATUSES.filter(status => counts[status]).map(
        status => ({
          value: status as string,
          label: `${this.$t(`customer-dashboard.giftcard.${status}`)} (${
            counts[status]
          })`
        })
      );

      return [
        {
          value: "all",
          label: `${this.$t("customer-dashboard.giftcard.filter-all")} (${
            this.validGiftcards.length
          })`
        },
        ...present
      ];
    },

    filteredGiftcards(): GiftcardObject[] {
      if (this.filter === "all") return this.validGiftcards;

      return this.validGiftcards.filter(
        giftcard => getGiftcardStatus(giftcard) === this.filter
      );
    },

    visibleGiftcards(): GiftcardObject[] {
      return this.filteredGiftcards.slice(0, this.visibleCount);
    },

    emptyMessage(): string {
      if (this.validGiftcards.length && this.filter !== "all")
        return this.$t("customer-dashboard.giftcard.no-matches").toString();

      return this.$t("customer-dashboard.giftcard.no-giftcards").toString();
    }
  },
  watch: {
    giftcards() {
      this.filter = "all";
      this.resetPaging();
    }
  },
  methods: {
    resetPaging(): void {
      this.visibleCount = this.pageSize;
    },

    loadMore(): void {
      const firstNewIndex = this.visibleCount;
      this.visibleCount += this.pageSize;

      /**
       * Move to the first card of the new batch. Without this the focus stays
       * on a button that may have just disappeared, dropping keyboard users
       * back at the top of the page.
       */
      this.$nextTick(() => {
        const cards = this.$refs.cards as Vue[] | undefined;
        const target = cards && cards[firstNewIndex];
        if (target) (target.$el as HTMLElement).focus();
      });
    }
  }
});
